import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Shield, BarChart3, FileText, Headphones, Lock } from 'lucide-react';

const PortalLoginPage = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const res = await fetch('/api/portal-login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() }),
            });
            const json = await res.json();
            if (res.status === 429) {
                setError('Too many attempts. Please wait a few minutes.');
            } else if (json.token) {
                // Full page load, not a router push: a third-party script loaded on a public page
                // (ad tracking) must never be running when the portal token is in the URL.
                window.location.assign(`/onboard/${json.token}?email=${encodeURIComponent(email.trim())}`);
            } else {
                setError(json.error || "We couldn't find an account with that email. If you're a new client, check your welcome email for your portal link.");
            }
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const features = [
        {
            icon: <FileText className="w-5 h-5" />,
            title: 'Digital Intake',
            desc: 'Complete your business questionnaire and onboarding paperwork — all in one place.',
        },
        {
            icon: <Lock className="w-5 h-5" />,
            title: 'Secure Credential Handoff',
            desc: 'Share passwords and API keys through encrypted, self-destructing links.',
        },
        {
            icon: <BarChart3 className="w-5 h-5" />,
            title: 'Live Build Tracker',
            desc: 'Watch your system come together in real time — every step visible, every milestone tracked.',
        },
        {
            icon: <Headphones className="w-5 h-5" />,
            title: 'Direct Support',
            desc: 'Message your dedicated project manager directly from your portal.',
        },
    ];

    return (
        <div className="min-h-screen pt-32 pb-20 px-6">
            <div className="max-w-5xl mx-auto">
                {/* Hero Section */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 bg-[#7B61FF]/10 border border-[#7B61FF]/20 rounded-full px-4 py-1.5 mb-6">
                        <Shield className="w-4 h-4 text-[#7B61FF]" />
                        <span className="text-[#7B61FF] text-xs font-bold tracking-wide uppercase">Client Portal</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                        Your Build.<br />
                        <span className="text-[#7B61FF]">Your Dashboard.</span>
                    </h1>
                    <p className="text-[#F0EFF4]/60 text-lg max-w-xl mx-auto">
                        Everything you need to onboard, track progress, and manage your RoofClaw system — secured behind one-time verification codes.
                    </p>
                </div>

                {/* Two-column layout */}
                <div className="grid md:grid-cols-2 gap-12 items-start">
                    {/* Left: Login Form */}
                    <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8">
                        <h2 className="font-bold text-[#F0EFF4] text-xl mb-2">Access Your Portal</h2>
                        <p className="text-[#F0EFF4]/50 text-sm mb-6">
                            Enter the email address you used when reserving your build. We'll send you a one-time access code.
                        </p>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="portal-email" className="block text-[#F0EFF4]/40 text-xs font-bold uppercase tracking-wider mb-2">
                                    Email Address
                                </label>
                                <input
                                    id="portal-email"
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    required
                                    className="w-full bg-[#F0EFF4]/5 border border-[#F0EFF4]/10 rounded-xl px-4 py-3 text-[#F0EFF4] placeholder-[#F0EFF4]/30 outline-none focus:border-[#7B61FF]/50 text-sm"
                                />
                            </div>
                            {error && (
                                <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">
                                    {error}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-[#7B61FF] hover:bg-[#6B51EF] disabled:opacity-60 text-[#F0EFF4] font-bold py-3 rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue to Portal'}
                            </button>
                        </form>
                        <p className="text-[#F0EFF4]/30 text-xs mt-4 text-center">
                            Not a client yet?{' '}
                            <Link to="/#reserve" className="text-[#7B61FF]/70 hover:text-[#7B61FF] transition-colors">
                                Reserve your build →
                            </Link>
                        </p>
                    </div>

                    {/* Right: Feature List */}
                    <div className="space-y-6">
                        <h3 className="text-[#F0EFF4]/40 text-xs font-bold uppercase tracking-wider mb-4">
                            What's Inside Your Portal
                        </h3>
                        {features.map((f, i) => (
                            <div key={i} className="flex gap-4 items-start">
                                <div className="w-10 h-10 rounded-xl bg-[#7B61FF]/10 flex items-center justify-center text-[#7B61FF] shrink-0">
                                    {f.icon}
                                </div>
                                <div>
                                    <h4 className="font-bold text-[#F0EFF4] text-sm mb-1">{f.title}</h4>
                                    <p className="text-[#F0EFF4]/50 text-sm leading-relaxed">{f.desc}</p>
                                </div>
                            </div>
                        ))}
                        <div className="mt-8 p-4 bg-[#7B61FF]/5 border border-[#7B61FF]/10 rounded-xl">
                            <p className="text-[#F0EFF4]/60 text-xs leading-relaxed">
                                <span className="text-[#7B61FF] font-bold">🔒 Secured by OTP.</span>{' '}
                                Every portal session requires a one-time code sent to your email. No passwords to remember, no credentials to leak.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PortalLoginPage;
